/**
 * @file     propertiesHref.ts
 * @purpose  Builds a /properties href from named params. Free of the place tables so search boxes and heroes can link to the map without bundling them.
 */

export type QueryValue = string | number | boolean | null | undefined | readonly (string | number)[];

/**
 * `/properties` href from named params. Arrays repeat the key (`region=a&region=b`),
 * `true` becomes `1`, empty and non-finite values drop. Callers round computed
 * amounts themselves. Use this instead of concatenating
 * `/properties?` by hand so every sender speaks the map's param names.
 */
export function propertiesHref(query: Readonly<Record<string, QueryValue>> = {}): string {
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value == null || value === false || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) if (v !== '' && v != null) sp.append(key, String(v));
    } else if (value === true) {
      sp.set(key, '1');
    } else if (typeof value === 'number') {
      if (Number.isFinite(value)) sp.set(key, String(value));
    } else {
      sp.set(key, String(value));
    }
  }
  return propertiesHrefFromParams(sp);
}

/** `/properties` href for params already serialized by the filter codec. */
export function propertiesHrefFromParams(sp: URLSearchParams): string {
  const qs = sp.toString();
  return qs ? `/properties?${qs}` : '/properties';
}
